import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/api';

export interface TagData {
  id: string;
  name: string;
  color: string;
  count: number;
}

export interface TagStats {
  predefinedTags: TagData[];
  dynamicTags: TagData[];
  totalTags: number;
  totalTaggedRooms: number;
  lastUpdated: string;
}

export interface UseTagStatsProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
  onError?: (error: string) => void;
}

export const useTagStats = ({
  autoRefresh = true,
  refreshInterval = 60000, // 1分
  onError,
}: UseTagStatsProps = {}) => {
  const [stats, setStats] = useState<TagStats>({
    predefinedTags: [],
    dynamicTags: [],
    totalTags: 0,
    totalTaggedRooms: 0,
    lastUpdated: new Date().toISOString(),
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // タグ統計データを取得
  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      
      const response = await apiClient.getTagStats();
      
      if (response.success && response.tags) {
        // APIレスポンスからタグデータを変換
        const dynamicTags: TagData[] = response.tags.map((tag: any) => ({
          id: tag.tag || tag.name,
          name: tag.tag || tag.name,
          color: '#2196f3', // デフォルトの色
          count: parseInt(tag.count || 0),
        }));

        setStats({
          predefinedTags: [],
          dynamicTags,
          totalTags: dynamicTags.length,
          totalTaggedRooms: dynamicTags.reduce((sum, tag) => sum + tag.count, 0),
          lastUpdated: new Date().toISOString(),
        });
        setLastUpdated(new Date());
      } else {
        throw new Error('タグ統計の取得に失敗しました');
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'タグ統計の取得に失敗しました';
      setError(errorMessage);
      onError?.(errorMessage);
      
      // エラー時はダミーデータを表示
      setStats({
        predefinedTags: [
          { id: 'urgent', name: '緊急', color: '#f44336', count: 0 },
          { id: 'important', name: '重要', color: '#ff9800', count: 0 },
          { id: 'followup', name: 'フォローアップ', color: '#2196f3', count: 0 },
          { id: 'resolved', name: '解決済み', color: '#4caf50', count: 0 },
          { id: 'escalated', name: 'エスカレーション', color: '#9c27b0', count: 0 }
        ],
        dynamicTags: [],
        totalTags: 5,
        totalTaggedRooms: 0,
        lastUpdated: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  }, [onError]);

  // 初回読み込み
  useEffect(() => {
    fetchStats();
  }, []); // 依存配列を空にして初回のみ実行

  // 自動更新
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]); // fetchStatsを依存配列から削除して無限ループを防止

  // 手動更新
  const refresh = useCallback(() => {
    setLoading(true);
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    lastUpdated,
    refresh,
  };
};

export default useTagStats;
